import React, { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import ChatSidebar from "./ChatSidebar";
import MessageList from "./MessageList";
import InputField from "./InputField";
import WalletInfo from "./WalletInfo";
import HeaderContainer from "../Header";
import { getChatHistory, saveChatHistory } from "./chatHistoryStorage";
import { getApiUrl } from "../../config";
import { env, useGlobalState } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { getToken } from "../../helpers/tokenManager";


// Types for the new agent backend
interface ChatStartResponse {
  success: boolean;
  conversationId: string;
  userId: string;
  status: string;
  message: string;
}

interface ChatPollResponse {
  hasUpdate: boolean;
  response: string;
  status: string;
  isComplete: boolean;
}

// Interface defined in chatHistoryStorage.ts

// Agent backend handles model selection internally

const ChatContainer: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);

  let {
    info,
    setChatHistory,
    chatHistory,
    selectedChatId,
    setSelectedChatId,
    messages,
    setMessages,
    isSignedIn,
    handleLogout,
  } = useGlobalState();

  // Track the current user identity to avoid excessive refreshes - memoize to prevent unnecessary recalculations
  const currentUserId = useMemo(() => 
    info?.email || info?.walletAddress || "anonymousweb", 
    [info?.email, info?.walletAddress]
  );
  const previousUserIdRef = useRef<string | null>(null);
  const hasInitiallyLoadedRef = useRef<boolean>(false);
  const currentSessionMessagesRef = useRef<{[key: string]: any[]}>({});
  
  // Clean up session cache when user changes (to prevent memory leaks)
  useEffect(() => {
    currentSessionMessagesRef.current = {};
  }, [currentUserId]);

  useEffect(() => {
    // Initial load or when user identity actually changes
    if (!hasInitiallyLoadedRef.current || previousUserIdRef.current !== currentUserId) {
      if (process.env.NODE_ENV === 'development') console.log(`[ChatContainer] ${!hasInitiallyLoadedRef.current ? 'Initial load' : `User identity changed from "${previousUserIdRef.current}" to "${currentUserId}"`}, fetching chat history`);
      
      const fetchChatHistory = async () => {
        const history = await getChatHistory();
        setChatHistory(history);
        hasInitiallyLoadedRef.current = true;
        previousUserIdRef.current = currentUserId;
      };

      fetchChatHistory();
    }
  }, [setChatHistory, currentUserId]); // Depend on derived stable user ID

  const { showErrorMessage } = useModalState();

  const handleSendMessage = async () => {
    if (loading || inputValue.trim() === "") return;

    const userMessage = {
      id: Date.now(),
      sender: "User",
      text: inputValue.trim(),
      date: new Date(),
    };

    setInputValue("");
    scrollToBottom();

    let currentChatId = selectedChatId;
    let conversationId: string;

    if (currentChatId === null) {
      // Create new conversation with agent backend
      try {
        const bearerToken = getToken();
        const userId = info?.email || info?.walletAddress || "anonymousweb"; // Use user ID from global state
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`[ChatContainer] Creating new conversation - info object:`, info);
          console.log(`[ChatContainer] Using user ID: "${userId}" (email: "${info?.email}", walletAddress: "${info?.walletAddress}")`);
          console.log("Creating new conversation...");
        }
        const createConversationResponse = await axios.post(
          `${getApiUrl(env)}/agent/users/conversations`,
          {
            userId,
            title: userMessage.text.length > 50 ? userMessage.text.substring(0, 50) + "..." : userMessage.text,
          },
          {
            headers: { Authorization: `Bearer ${bearerToken}` },
          }
        );

        conversationId = createConversationResponse.data.conversation.id;
        if (process.env.NODE_ENV === 'development') console.log(`Created conversation: ${conversationId}`);

        // Create new chat with the agent conversation ID
        const newChatId = Date.now();
        currentChatId = newChatId;

        setChatHistory((prevChatHistory: any) => {
          const newChat = {
            id: newChatId,
            agentConversationId: conversationId, // Store the agent backend conversation ID
            title: userMessage.text.length > 50 ? userMessage.text.substring(0, 50) + "..." : userMessage.text,
            messages: [], // Don't store messages locally - backend is source of truth
          };
          return [...prevChatHistory, newChat];
        });

        setSelectedChatId(currentChatId);
      } catch (createError: any) {
        if (process.env.NODE_ENV === 'development') console.error("Failed to create conversation:", createError);
        showErrorMessage("Failed to create new conversation. Please try again.");
        return;
      }
    } else {
      // Get existing conversation ID or create one if it doesn't exist
      const existingChat = chatHistory.find((chat: any) => chat.id === currentChatId);
      
      if (!existingChat?.agentConversationId) {
        // This is a new chat that was created from the sidebar but doesn't have an agent conversation yet
        // Create a new conversation with the agent backend
        try {
          const bearerToken = getToken();
        const userId = info?.email || info?.walletAddress || "anonymousweb"; // Use user ID from global state
          
          if (process.env.NODE_ENV === 'development') console.log("Creating conversation for existing chat...");
          const createConversationResponse = await axios.post(
            `${getApiUrl(env)}/agent/users/conversations`,
            {
              userId,
              title: userMessage.text.length > 50 ? userMessage.text.substring(0, 50) + "..." : userMessage.text,
            },
            {
              headers: { Authorization: `Bearer ${bearerToken}` },
            }
          );

          conversationId = createConversationResponse.data.conversation.id;
          if (process.env.NODE_ENV === 'development') console.log(`Created conversation for existing chat: ${conversationId}`);
          
          // Update the existing chat with the agent conversation ID and new title
          setChatHistory((prevChatHistory: any) =>
            prevChatHistory.map((chat: any) => {
              if (chat.id === currentChatId) {
                const newTitle = userMessage.text.length > 50 ? userMessage.text.substring(0, 50) + "..." : userMessage.text;
                return { 
                  ...chat, 
                  messages: [], // Don't store messages locally - backend is source of truth
                  agentConversationId: conversationId,
                  title: newTitle
                };
              }
              return chat;
            }),
          );
          
        } catch (createError: any) {
          if (process.env.NODE_ENV === 'development') console.error("Failed to create conversation for existing chat:", createError);
          showErrorMessage("Failed to create conversation. Please try again.");
          return;
        }
      } else {
        conversationId = existingChat.agentConversationId;

        // Don't update chat history with messages - backend is source of truth
        // Messages will be loaded from backend when conversation is selected
      }
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      const bearerToken = getToken();
        const userId = info?.email || info?.walletAddress || "anonymousweb"; // Use user ID from global state

      if (process.env.NODE_ENV === 'development') console.log(`Starting chat with conversation ID: ${conversationId}`);
      
      // Start the chat with the new agent backend
      const startResponse = await axios.post(
        `${getApiUrl(env)}/agent/chat/start`,
        {
          message: userMessage.text,
          conversationId,
          userId
        },
        {
          headers: { Authorization: `Bearer ${bearerToken}` },
        }
      );

      const startData: ChatStartResponse = startResponse.data;
      
      if (!startData.success) {
        throw new Error(`Failed to start chat: ${startData.message}`);
      }

      if (process.env.NODE_ENV === 'development') console.log("Chat started, beginning polling...");

      // Add user message and temporary "thinking" message to current UI state only
      const thinkingMessage = {
        id: Date.now() + 1,
        sender: "CoinCap",
        text: "🤖 Processing your request...",
        date: new Date(),
        model: "Agent", // Agent backend handles model selection
        duration: 0,
        isTemporary: true,
      };

          // Update UI state directly instead of chat history (backend manages messages)
          const newMessages = [...messages, userMessage, thinkingMessage];
          setMessages(newMessages);
          
          // Cache the current session's messages for this conversation
          if (selectedChatId) {
            currentSessionMessagesRef.current[selectedChatId.toString()] = newMessages;
          }      // Poll for updates
      let isComplete = false;
      let pollCount = 0;
      let lastResponse = "";
      const maxPolls = 60; // Maximum 60 polls (about 2 minutes with 2s intervals)
      const minUpdateInterval = 1500; // Minimum 1.5 seconds between updates
      let lastUpdateTime = Date.now();
      
      while (!isComplete && pollCount < maxPolls) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds between polls
        
        try {
          const encodedConversationId = encodeURIComponent(conversationId);
          const encodedUserId = encodeURIComponent(userId);
          const pollUrl = `${getApiUrl(env)}/agent/chat/${encodedConversationId}/poll?userId=${encodedUserId}`;
          
          const pollResponse = await axios.get(pollUrl, {
            headers: { Authorization: `Bearer ${bearerToken}` },
          });

          const pollData: ChatPollResponse = pollResponse.data;
          if (process.env.NODE_ENV === 'development') console.log(`Poll ${pollCount + 1}: hasUpdate=${pollData.hasUpdate}, isComplete=${pollData.isComplete}`);
          
          if (pollData.hasUpdate && pollData.response && pollData.response !== lastResponse) {
            const now = Date.now();
            
            // Only update if enough time has passed or if this is the final response
            if (pollData.isComplete || (now - lastUpdateTime) >= minUpdateInterval) {
              const responseTime = pollData.isComplete ? Date.now() - startTime : 0;
              
              const botMessage = {
                id: Date.now() + 1,
                sender: "CoinCap",
                text: pollData.response,
                date: new Date(),
                model: "Agent", // Agent backend handles model selection
                duration: responseTime,
                isTemporary: !pollData.isComplete,
              };

              // Update UI state directly - remove temporary messages and add/update response
              setMessages(prevMessages => {
                const filteredMessages = prevMessages.filter((msg: any) => !msg.isTemporary);
                const newMessages = [...filteredMessages, botMessage];
                
                // Cache the current session's messages for this conversation
                if (selectedChatId) {
                  currentSessionMessagesRef.current[selectedChatId.toString()] = newMessages;
                }
                
                return newMessages;
              });
              
              lastUpdateTime = now;
              if (process.env.NODE_ENV === 'development') console.log(`Updated message: ${pollData.response.substring(0, 100)}...`);
            }
            
            lastResponse = pollData.response;
          }
          
          isComplete = pollData.isComplete;
          pollCount++;
          
        } catch (pollError: any) {
          if (process.env.NODE_ENV === 'development') console.error(`Error polling for updates (attempt ${pollCount + 1}/${maxPolls}):`, pollError.message);
          pollCount++;
        }
      }
      
      if (pollCount >= maxPolls && !isComplete) {
        // Timeout - update with whatever we have
        const timeoutMessage = {
          id: Date.now() + 1,
          sender: "CoinCap",
          text: lastResponse + "\n\n⚠️ Response timed out after 2 minutes.",
          date: new Date(),
          model: "Agent", // Agent backend handles model selection
          duration: Date.now() - startTime,
          isTemporary: false,
        };

        // Update UI state directly for timeout
        setMessages(prevMessages => {
          const filteredMessages = prevMessages.filter((msg: any) => !msg.isTemporary);
          const newMessages = [...filteredMessages, timeoutMessage];
          
          // Cache the current session's messages for this conversation
          if (selectedChatId) {
            currentSessionMessagesRef.current[selectedChatId.toString()] = newMessages;
          }
          
          return newMessages;
        });
      }

    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') console.error("Error calling agent chat service:", error.message);
      
      // Remove temporary message and add error message
      const errorMessage = {
        id: Date.now() + 1,
        sender: "CoinCap",
        text: "❌ Sorry, I'm having trouble connecting to the chat service. Please try again in a moment.",
        date: new Date(),
        model: "Agent", // Agent backend handles model selection
        duration: Date.now() - startTime,
        isTemporary: false,
      };

      // Update UI state directly for error
      setMessages(prevMessages => {
        const filteredMessages = prevMessages.filter((msg: any) => !msg.isTemporary);
        const newMessages = [...filteredMessages, errorMessage];
        
        // Cache the current session's messages for this conversation
        if (selectedChatId) {
          currentSessionMessagesRef.current[selectedChatId.toString()] = newMessages;
        }
        
        return newMessages;
      });
      
      showErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
      scrollToBottom();
      
      // Note: We don't refresh chat history immediately after completion to avoid
      // disrupting the user's current view. The backend's message culling will be
      // reflected when they navigate to other conversations or reload the page.
    }
  };

  const scrollToBottom = () => {
    if (chatEndRef.current)
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  };

  // Find the selected chat and extract only the relevant properties to minimize re-renders
  const selectedChat = useMemo(() => 
    selectedChatId ? chatHistory.find((chat: any) => chat.id === selectedChatId) : null,
    [selectedChatId, chatHistory]
  );

  useEffect(() => {
    const loadMessagesForSelectedChat = async () => {
      if (selectedChatId !== null && selectedChat) {
        // First check if we have current session messages for this conversation
        const sessionMessages = currentSessionMessagesRef.current[selectedChatId.toString()];
        if (sessionMessages && sessionMessages.length > 0) {
          if (process.env.NODE_ENV === 'development') console.log(`[ChatContainer] Loading ${sessionMessages.length} messages from current session cache`);
          setMessages(sessionMessages);
          return;
        }

        if (selectedChat.agentConversationId) {
          // Fetch fresh messages from the backend to ensure they're in sync (respecting backend's message culling)
          if (process.env.NODE_ENV === 'development') console.log(`Loading messages for conversation: ${selectedChat.agentConversationId}`);
          
          try {
            const bearerToken = getToken();
            
            if (process.env.NODE_ENV === 'development') console.log(`[ChatContainer] Loading messages for user ID: "${currentUserId}" and conversation: "${selectedChat.agentConversationId}"`);
            
            const response = await axios.get(
              `${getApiUrl(env)}/agent/users/${encodeURIComponent(currentUserId)}/conversations/${encodeURIComponent(selectedChat.agentConversationId)}`,
              {
                headers: { Authorization: `Bearer ${bearerToken}` },
              }
            );
            
            if (response.data && response.data.messages) {
              // Convert backend messages to frontend format
              const backendMessages = response.data.messages.map((msg: any, index: number) => ({
                id: Date.now() + index, // Generate unique IDs for frontend
                sender: msg.role === 'user' ? 'User' : 'CoinCap',
                text: msg.content,
                date: new Date(msg.timestamp || Date.now()),
                model: msg.role === 'assistant' ? 'Agent' : undefined,
                duration: msg.duration || 0,
                isTemporary: false,
              }));
              
              if (process.env.NODE_ENV === 'development') console.log(`Loaded ${backendMessages.length} messages from backend (backend may have culled older messages)`);
              setMessages(backendMessages);
            } else {
              if (process.env.NODE_ENV === 'development') console.log('No messages found in backend response');
              setMessages([]);
            }
          } catch (error: any) {
            if (process.env.NODE_ENV === 'development') console.error('Failed to load messages from backend:', error.message);
            // Fallback to cached messages if backend fails
            setMessages(selectedChat.messages || []);
          }
        } else if (selectedChat) {
          // Use cached messages for chats without agent conversation ID (shouldn't happen with new flow)
          setMessages(selectedChat.messages || []);
        } else {
          setMessages([]);
        }
      } else {
        setMessages([]);
      }
    };
    
    loadMessagesForSelectedChat();
  }, [selectedChatId, selectedChat, setMessages, currentUserId]); // Use memoized selectedChat

  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    saveChatHistory(chatHistory);
  }, [chatHistory]);

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        maxWidth: "1392px",
        margin: "0 auto",
        paddingTop: "24px",
        paddingBottom: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      <HeaderContainer behavior="chatbot" />
      {/* Main container to center the content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
          padding: "20px",
        }}
      >
        {/* Content wrapper for max width */}
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          {/* Main chat area */}
          <div
            style={{
              flex: 1,
              display: "flex",
              gap: "24px",
              padding: "40px",
              paddingTop: "16px",
              minHeight: 0,
              background: "transparent",
              borderRadius: "24px",
              boxShadow: "none",
            }}
          >
            {/* Left Column */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "320px",
                height: "100%",
                minHeight: 0,
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <ChatSidebar />
              </div>
              {isSignedIn() && (
                <div style={{ flexShrink: 0, marginTop: "16px" }}>
                  <WalletInfo
                    disconnectWallet={handleLogout}
                    info={info}
                  />
                </div>
              )}
            </div>

            {/* Right Column */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                background: "#ffffff",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e4e4e7",
                borderRadius: "16px",
                padding: "40px",
                minHeight: 0,
              }}
            >
              {/* Title and Dropdown */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "24px",
                }}
              >
                <h1
                  style={{
                    fontSize: "24px",
                    fontWeight: "800",
                    color: "#18181b",
                    margin: 0,
                  }}
                >
                  {
                    chatHistory.find((chat: any) => chat.id === selectedChatId)
                      ?.title
                  }
                </h1>
              </div>

              <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                <MessageList messages={messages} chatEndRef={chatEndRef} />
              </div>
              <div style={{ flexShrink: 0 }}>
                <InputField
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  handleSendMessage={handleSendMessage}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatContainer;
